import styled from "@emotion/styled";
import { calculateOrderLiquidationPrice } from "@maths/positionMath";
import { Flex } from "@radix-ui/themes";
import { observer } from "mobx-react-lite";
import React from "react";
import { useTranslation } from "react-i18next";

import { useLiqPrice } from "@components/hooks/useLiqPrice";
import { theme } from "@themes/themes";

import { useStores } from "@stores";

import type { Position } from "@src/api";

import { BN } from "@utils/BN";
import { formatNumber } from "@utils/Format";

import { Accordion, Separator } from "@src/ui-kit";

import { ValueText } from "./styled";

// ---------------------------------------------------------------------------
// OrderDetails: static Accordion shell (React.memo) + observer content.
// The Accordion Provider / Trigger / Header never re-render on price ticks;
// only the tiny content observer updates.
// ---------------------------------------------------------------------------
export interface TradeboxOrderDetailsProps {
  size: BN;
  direction: "long" | "short";
  leverage: number;
  limitPrice: BN;
  orderType: "limit" | "market";
  total: BN;
}

export const TradeboxOrderDetails: React.FC<TradeboxOrderDetailsProps> = React.memo(
  ({ size, direction, leverage, limitPrice, orderType, total }) => {
    const { t } = useTranslation();
    return (
      <Accordion data-testid="tradebox:details:toggle" label={t("orderDetails.title")} defaultOpen>
        <TradeboxOrderDetailsContent
          size={size}
          direction={direction}
          leverage={leverage}
          limitPrice={limitPrice}
          orderType={orderType}
          total={total}
        />
      </Accordion>
    );
  },
);

const TradeboxOrderDetailsContent: React.FC<TradeboxOrderDetailsProps> = observer(
  ({ size, direction, leverage, limitPrice, orderType, total }) => {
    const { t } = useTranslation();
    const { accountStore, marketStore, myOrdersStore } = useStores();
    const { calc } = useLiqPrice();

    const activeMarket = marketStore.activeMarket;
    const marketConfig = marketStore.getMarketConfig(activeMarket?.addr ?? "");
    const instrumentInfo = marketStore.getInstrumentInfo(activeMarket?.symbol ?? "");
    const quoteSymbol = marketStore.quoteToken?.symbol;

    const margin = total.dividedBy(marketStore.currentLeverage);
    const maintenanceMargin = new BN(activeMarket?.maintenance_margin_ratio ?? 0);
    const makerFeeRate = (instrumentInfo?.maker_fee_rate ?? activeMarket?.maker_fee_rate ?? 0) / 1_000_000;
    const takerFeeRate = (instrumentInfo?.taker_fee_rate ?? activeMarket?.taker_fee_rate ?? 0) / 1_000_000;
    const makerPct = (makerFeeRate * 100).toFixed(2);
    const takerPct = (takerFeeRate * 100).toFixed(2);

    // Cross-margin liquidation price for the order
    const estLiquidationPrice = React.useMemo(() => {
      const activeTradingAccount = accountStore.activeTradingAccount;
      const portfolio = accountStore.portfolio;

      if (
        !activeMarket ||
        !marketStore.baseToken ||
        !marketStore.quoteToken ||
        !activeTradingAccount ||
        !portfolio ||
        size.isZero()
      ) {
        const mmr = activeMarket ? new BN(activeMarket.maintenance_margin_ratio) : new BN(0.01);
        const priceToUse = orderType === "market" ? new BN(marketStore.marketPrice) : limitPrice;
        return calc({
          entryPrice: priceToUse,
          leverage,
          mmr,
          size: size.abs(),
          side: direction,
        });
      }

      const existingPositions: Position[] = myOrdersStore.openPosition ?? [];
      const maintenanceMarginRatio = Number(activeMarket.maintenance_margin_ratio);
      const fee = Number(activeMarket.taker_fee_rate) / 1_000_000;
      const accountEquity = Number(portfolio.equity);
      const priceToUse = orderType === "market" ? marketStore.marketPrice : limitPrice;

      return calculateOrderLiquidationPrice(
        size,
        direction === "long" ? "buy" : "sell",
        priceToUse,
        leverage,
        activeMarket.addr,
        marketStore.marketPrice,
        existingPositions,
        marketStore.allMarkets,
        accountEquity,
        activeMarket.base_decimals,
        activeMarket.quote_decimals,
        maintenanceMarginRatio,
        fee,
      );
    }, [
      accountStore.activeTradingAccount,
      accountStore.portfolio,
      calc,
      direction,
      leverage,
      limitPrice,
      activeMarket,
      marketStore.allMarkets,
      marketStore.baseToken,
      marketStore.marketPrice,
      marketStore.quoteToken,
      myOrdersStore.openPosition,
      orderType,
      size,
    ]);

    return (
      <Flex direction="column" gap="8px">
        <Separator color="accent-7" size="4" />
        <Flex align="center" justify="between">
          <OrderDetailsLabel>{t("orderDetails.marginRequired")}</OrderDetailsLabel>
          <ValueText data-testid="tradebox:details:margin-required:value">
            {formatNumber(margin, {
              decimals: marketConfig.pricePrecision,
              toSignificant: false,
            })}{" "}
            <span>{quoteSymbol}</span>
          </ValueText>
        </Flex>
        <Flex align="center" justify="between">
          <OrderDetailsLabel>{t("common.fee")}</OrderDetailsLabel>
          <ValueText data-testid="tradebox:details:fee:value">
            {t("orderDetails.feeBreakdown", { takerPct, makerPct })}
          </ValueText>
        </Flex>
        <Flex align="center" justify="between">
          <OrderDetailsLabel>{t("orderDetails.positionValue")}</OrderDetailsLabel>
          <ValueText data-testid="tradebox:details:position-value:value">
            {formatNumber(total, { decimals: 2, toSignificant: false })} <span>{quoteSymbol}</span>
          </ValueText>
        </Flex>

        <Separator color="gray-5" size="4" />

        <Flex align="center" justify="between">
          <OrderDetailsLabel>{t("orderDetails.liquidationPrice")}</OrderDetailsLabel>
          <OrderDetailsProfitText data-testid="tradebox:details:liq-price:value" isProfit={false}>
            {estLiquidationPrice.lt(0)
              ? "-"
              : formatNumber(estLiquidationPrice, {
                  decimals: marketConfig.pricePrecision,
                  toSignificant: false,
                })}{" "}
          </OrderDetailsProfitText>
        </Flex>
        <Flex align="center" justify="between">
          <OrderDetailsLabel>{t("orderDetails.maintenanceMargin")}</OrderDetailsLabel>
          <ValueText data-testid="tradebox:details:mm:value">
            {formatNumber(total.multipliedBy(maintenanceMargin), { decimals: 2 })} <span>{quoteSymbol}</span>
          </ValueText>
        </Flex>
      </Flex>
    );
  },
);

// --- Styled components ---
const OrderDetailsLabel = styled.span`
  ${theme.font.body12};
  color: ${theme.color("gray-11")};
`;

const OrderDetailsProfitText = styled.span<{ isProfit?: boolean }>`
  ${theme.font.body12};
  color: ${({ isProfit }) =>
    isProfit === true
      ? theme.color("green-strong")
      : isProfit === false
        ? theme.color("red-strong")
        : theme.color("accent-contrast")};
`;
